/**
 * KAN-877: части кнопки "Позвонить" (только рендер, логика в хуках).
 *
 * CallRow - кнопка с номером рядом (прежний вид KAN-833/863).
 * CallTargetPicker - выбор адресата в строках списков: на телефоне лист
 * снизу, на широком экране меню под кнопкой; Escape и тап мимо закрывают.
 * Модальный: Tab ходит по кругу внутри, после закрытия фокус на кнопке.
 */
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Loader2, Phone } from 'lucide-react';
import { callTargetRoleText, callTargetTitle } from '@/lib/callTargets';
import type { CallTarget, CallTargetKind } from '@/services/api/callTargets';

export type CallButtonSize = 'sm' | 'lg';

const BUTTON_SIZE = {
  sm: 'gap-1 rounded px-2 py-0.5',
  lg: 'min-h-[44px] gap-2 rounded-lg px-4 py-2 text-base',
} as const;

const ICON_SIZE = { sm: 'h-3.5 w-3.5', lg: 'h-5 w-5' } as const;

interface CallRowProps {
  testId: string;
  label: string;
  /** Номер рядом с кнопкой; пусто - без номера. */
  phone?: string;
  title: string;
  disabled: boolean;
  pending: boolean;
  size: CallButtonSize;
  onClick: () => void;
  className?: string;
  children?: ReactNode;
  buttonRef?: RefObject<HTMLButtonElement>;
}

export function CallRow({
  testId,
  label,
  phone,
  title,
  disabled,
  pending,
  size,
  onClick,
  className = '',
  children,
  buttonRef,
}: CallRowProps) {
  return (
    <span className={`inline-flex flex-wrap items-center gap-2 text-sm ${className}`}>
      <button
        type="button"
        ref={buttonRef}
        data-testid={testId}
        onClick={onClick}
        disabled={disabled || pending}
        title={title}
        className={`inline-flex items-center border border-blue-300 bg-white font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_SIZE[size]}`}
      >
        {pending ? (
          <Loader2 className={`${ICON_SIZE[size]} animate-spin`} aria-hidden="true" />
        ) : (
          <Phone className={ICON_SIZE[size]} aria-hidden="true" />
        )}
        {label}
      </button>
      {phone && (
        <span data-testid={`${testId}-phone`} className="select-all text-gray-700">
          {phone}
        </span>
      )}
      {children}
    </span>
  );
}

interface CallTargetPickerProps {
  testId: string;
  targets: CallTarget[];
  onChoose: (target: CallTargetKind) => void;
  onClose: () => void;
  /** Куда вернуть фокус после закрытия (кнопка "Позвонить"). */
  returnFocusRef?: RefObject<HTMLElement>;
}

/** Tab и Shift+Tab по кругу между кнопками выбора. */
function trapTab(event: KeyboardEvent, dialog: HTMLElement | null) {
  if (event.key !== 'Tab' || !dialog) return;
  const items = Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled])'));
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !dialog.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
    event.preventDefault();
    first.focus();
  }
}

export function CallTargetPicker({ testId, targets, onChoose, onClose, returnFocusRef }: CallTargetPickerProps) {
  const firstRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else trapTab(event, dialogRef.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    const target = returnFocusRef?.current;
    return () => target?.focus();
  }, [returnFocusRef]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 sm:bg-transparent" aria-hidden="true" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Кому позвонить"
        data-testid={`${testId}-picker`}
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white p-3 pb-6 text-left shadow-lg sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-1 sm:w-72 sm:rounded-lg sm:border sm:border-gray-200 sm:pb-3"
      >
        <p className="px-2 pb-2 text-sm font-medium text-gray-900">Кому позвонить</p>
        <ul className="divide-y divide-gray-100">
          {targets.map((target, index) => (
            <li key={`${target.target}-${index}`}>
              <button
                type="button"
                ref={index === 0 ? firstRef : undefined}
                data-testid={`${testId}-pick-${target.target}`}
                onClick={() => onChoose(target.target)}
                className="flex min-h-[44px] w-full flex-col items-start rounded px-2 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              >
                <span className="font-medium text-gray-900">{callTargetTitle(target)}</span>
                <span className="text-xs text-gray-500">{callTargetRoleText(target)}</span>
                {target.phone && <span className="text-sm text-gray-700">{target.phone}</span>}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full rounded px-2 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          Отмена
        </button>
      </div>
    </>
  );
}
